import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { type PropertyDocumentKind, type PropertyFileResponse } from '@/shared/api';
import { requiresIssuedOn } from './akteKindLabels';
import { ReclassifyFields } from './ReclassifyFields';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  file: PropertyFileResponse;
  /** Vorbelegtes Ziel-Kind beim Cross-Category-Drop; ohne = aktuelles Kind der Datei. */
  suggestedKind?: PropertyDocumentKind | null;
  onReclassify: (kind: PropertyDocumentKind, issuedOn: string | null) => Promise<void>;
}

/** „Kategorie ändern" (Reclassify) — einzige Kind-Änderung, kategorieübergreifend (§4.7). */
export function ReclassifyDialog({ open, onOpenChange, file, suggestedKind, onReclassify }: Props) {
  const [kind, setKind] = useState<PropertyDocumentKind>(suggestedKind ?? file.kind);
  const [issuedOn, setIssuedOn] = useState(file.issuedOn ?? '');
  const [dateError, setDateError] = useState(false);
  const [busy, setBusy] = useState(false);

  const needsDate = requiresIssuedOn(kind) && !file.issuedOn;

  async function submit() {
    if (needsDate && !issuedOn) {
      setDateError(true);
      return;
    }
    setBusy(true);
    try {
      await onReclassify(kind, needsDate ? issuedOn : (file.issuedOn ?? null));
      onOpenChange(false);
    } catch {
      // Fehler wird als Toast gezeigt (Mutation); Dialog bleibt offen zur Korrektur.
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Kategorie ändern</DialogTitle>
          <DialogDescription>
            Bestimmen Sie neu, was für ein Dokument das ist. Achtung: das ändert die Bedeutung des
            Belegs (Finanz-Zuordnung, Sichtbarkeit).
          </DialogDescription>
        </DialogHeader>
        <ReclassifyFields
          idPrefix="reclassify"
          kind={kind}
          onKindChange={setKind}
          needsDate={needsDate}
          issuedOn={issuedOn}
          onIssuedOnChange={(v) => {
            setIssuedOn(v);
            setDateError(false);
          }}
          dateError={dateError}
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Abbrechen
          </Button>
          <Button variant="brand" loading={busy} onClick={submit}>
            Umklassifizieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
